import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle, AlertTriangle, Loader2, Bot } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { setupPriceBotWebhook, checkPriceBotWebhook } from '@/utils/setupPriceBotWebhook';

interface WebhookInfo {
  success: boolean;
  webhook_info?: {
    url: string;
    is_active: boolean;
    pending_update_count?: number;
    last_error_date?: number;
    last_error_message?: string;
  };
  error?: string;
}

export function PriceBotWebhookManager() {
  const [webhookInfo, setWebhookInfo] = useState<WebhookInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const { toast } = useToast();

  const checkWebhookStatus = async () => {
    setIsLoading(true);
    try {
      const result = await checkPriceBotWebhook();
      setWebhookInfo(result);
      
      if (!result.success) {
        toast({
          title: "Error",
          description: result.error || "Failed to check webhook status",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('Error checking price bot webhook status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to check webhook status",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setupWebhookHandler = async () => {
    setIsSettingUp(true);
    try {
      const result = await setupPriceBotWebhook();
      
      if (result.success) {
        toast({
          title: "Webhook setup successful",
          description: `Price bot webhook configured successfully`,
        });
        
        // Refresh webhook status
        await checkWebhookStatus();
      } else {
        toast({
          title: "Webhook setup failed",
          description: result.error || "Failed to set up webhook",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('Failed to set up price bot webhook:', error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSettingUp(false);
    }
  };

  const getStatusAlert = () => {
    if (!webhookInfo?.webhook_info) return null;

    const info = webhookInfo.webhook_info;

    if (info.is_active) {
      return (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Price Bot Webhook Active</AlertTitle>
          <AlertDescription>
            Your Telegram price bot webhook is properly configured and active.
            {info.pending_update_count && info.pending_update_count > 0 && (
              <div className="mt-2">
                Pending updates: {info.pending_update_count}
              </div>
            )}
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Price Bot Webhook Not Active</AlertTitle>
        <AlertDescription>
          Your Telegram price bot webhook is not set up. Click "Setup Webhook" to configure it.
          {info.last_error_message && (
            <div className="mt-2 text-sm">
              Last error: {info.last_error_message}
            </div>
          )}
        </AlertDescription>
      </Alert>
    );
  };

  React.useEffect(() => {
    checkWebhookStatus();
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          Price Bot Webhook Setup
        </CardTitle>
        <CardDescription>
          Configure the webhook for the Telegram price bot to receive messages
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {getStatusAlert()}
        
        {webhookInfo?.webhook_info && (
          <div className="space-y-2">
            <div className="text-sm">
              <strong>Webhook URL:</strong> {webhookInfo.webhook_info.url || 'Not set'}
            </div>
            <div className="text-sm">
              <strong>Status:</strong> {webhookInfo.webhook_info.is_active ? 'Active' : 'Inactive'}
            </div>
          </div>
        )}
        
        <div className="flex gap-2">
          <Button 
            onClick={checkWebhookStatus} 
            variant="outline" 
            disabled={isLoading || isSettingUp}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : (
              'Check Status'
            )}
          </Button>
          
          <Button 
            onClick={setupWebhookHandler} 
            disabled={isLoading || isSettingUp}
          >
            {isSettingUp ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Setting Up...
              </>
            ) : (
              'Setup Webhook'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}